import { makeAutoObservable } from "mobx";
import { MFormStore } from "src/components/MForm/MFormStore";

import { mfPath2str } from "../../MFormTypes";
import { MfNode } from "../../node";
import { cmpErrors } from "./cmpErrors";
import { cvtEntityErrorToRow, MfErrorRow, mfErrorToRow } from "./MfErrorRow";

export type MfErrorsListMode = "collapsed" | "expanded" | "hidden";

export type MfErrorsListColor = "red" | "white" | "yellow";

export class MfErrorsListStore {
  constructor(public form: MFormStore) {
    makeAutoObservable(this);
  }

  clear() {
    this.form.setExternalErrors([]);
    this.hide();
  }

  mode: MfErrorsListMode = "hidden";

  setMode(newMode: MfErrorsListMode) {
    this.mode = newMode;
  }

  expand() {
    this.setMode("expanded");
  }

  collapse() {
    this.setMode("collapsed");
  }

  hide() {
    this.setMode("hidden");
  }

  get color(): MfErrorsListColor {
    if (this.allErrors.some(({ warning }) => !warning)) {
      return "red";
    }
    return this.allErrors.length > 0 ? "yellow" : "white";
  }

  // Предельные значения высоты задаются классом .expandedBox
  // Значение высоты теоретически может выходить за эти пределы, но видимый результат - нет.
  height = 300;

  setHeight(newHeight: number) {
    this.height = newHeight;
  }

  changeHeight(newHeight: number) {
    this.setHeight(newHeight);
  }

  // filter
  filter = "";

  setFilter(value: string) {
    this.filter = value;
  }

  // ----

  get allErrors(): MfErrorRow[] {
    const frontErrors = Object.values(this.form.errors);
    const list = [
      ...frontErrors
        .filter(({ node }) => this.form.isTouched(mfPath2str(node.dataPath)))
        .map((error) => mfErrorToRow(error, this.form.root)),
      ...this.form.externalErrors.map((err) =>
        cvtEntityErrorToRow(err, this.form.root),
      ),
    ];
    // нужна уникальность по id
    const idDict: Record<string, MfErrorRow> = {};
    list.forEach((rec) => {
      idDict[rec.id] = rec;
    });
    return Object.values(idDict).sort(cmpErrors(this.fieldIndices));
  }

  // Сквозная нумерация полей
  get fieldIndices(): Record<string, number> {
    let lastIndex = 0;
    const result: Record<string, number> = {};
    const onNode = ({ dataPath, items }: MfNode) => {
      result[mfPath2str(dataPath)] = lastIndex++;
      if (items) {
        items.forEach(onNode);
      }
    };
    onNode(this.form.root);
    return result;
  }

  get visibleErrors(): MfErrorRow[] {
    const { filter } = this;
    return this.allErrors.filter(
      ({ attrName, message, typeName }) =>
        fnFilter(typeName, filter) ||
        fnFilter(attrName, filter) ||
        fnFilter(message, filter),
    );
  }
}

const fnFilter = (value: string | undefined, filter: string): boolean =>
  !filter || (value?.toLowerCase().includes(filter.toLowerCase()) ?? false);
